import type {
  PunchItem, PunchStatus, PunchBicType, PunchAction, PunchAssignee, PunchAssigneeInput, PunchStage, PunchWall,
  PunchTrade, PunchMedia, PunchSignoffs,
} from '@/lib/apiClient';

/**
 * The Punch List tab's logic, kept out of the components so it is tested as
 * logic: counts, filters, the by-sub table, which actions an item offers, and
 * the carry-over groups publishing shows. The server decides every rule; this
 * only mirrors it to show the right buttons (the API refuses anything else).
 */

/**
 * List stage labels, in ONE place (design handoff: "Keep the labels in one
 * constant so they're easy to change" — the wording is still open).
 */
export const STAGE_LABELS: Record<PunchStage, string> = {
  internal: 'E&W Internal Punch',
  published: 'Published Punch List',
  closed_out: 'Closed out',
};

export const STAGE_ORDER: PunchStage[] = ['internal', 'published', 'closed_out'];

export const STATUS_LABELS: Record<PunchStatus, string> = {
  new: 'New',
  open: 'Open',
  rejected: 'Rejected',
  ready_for_review: 'Ready for review',
  verified: 'Verified',
  closed: 'Closed',
};

export const WALL_LABELS: Record<PunchWall, string> = { north: 'North', south: 'South', east: 'East', west: 'West' };

/** Waiting on the sub (or not yet sent): the design's "Open" bucket. */
export const OPEN_BUCKET: readonly PunchStatus[] = ['new', 'open', 'rejected'];

export function isOpenStatus(s: PunchStatus): boolean {
  return s !== 'closed';
}

/**
 * Whose move it is, as the tab groups it: the ball with the ASSIGNEE is the
 * sub's move — unless the assignee is the E&W crew, which is our own work.
 * One rule for the counts and the filter, so a card and the table it filters
 * always agree (Codex on #565).
 */
export function ballGroup(i: Pick<PunchItem, 'bic' | 'assignee'>): PunchBicType {
  return i.bic.type === 'assignee' && i.assignee?.type === 'ew_crew' ? 'ew' : i.bic.type;
}

/** Case-insensitive grouping key, the same one the API groups by. */
export function assigneeKey(a: PunchAssignee): string {
  if (!a) return 'unassigned';
  return a.type === 'ew_crew' ? 'ew_crew' : `sub:${a.name.trim().toLowerCase()}`;
}

export function assigneeFromKey(key: string, items: PunchItem[]): PunchAssigneeInput | null {
  if (key === 'ew_crew') return { type: 'ew_crew' };
  const hit = items.find((i) => assigneeKey(i.assignee) === key);
  return hit?.assignee ?? null;
}

/** "Rm 104 · South", "Atrium", or "—". A bare number reads as a room. */
export function locationLabel(item: Pick<PunchItem, 'room' | 'wall'>): string {
  const room = item.room?.trim();
  const roomPart = room ? (/^\d+[a-z]?$/i.test(room) ? `Rm ${room}` : room) : '';
  const wallPart = item.wall ? WALL_LABELS[item.wall] : '';
  return [roomPart, wallPart].filter(Boolean).join(' · ') || '—';
}

export interface PunchSummary {
  total: number;
  open: number;            // new + open + rejected
  notSent: number;         // new
  readyForReview: number;
  awaitingApproval: number;
  closed: number;
  overdue: number;
  /** E&W's move: E&W holds the ball, or the E&W crew is doing the work. */
  onUs: { total: number; toVerify: number; toSend: number; withCrew: number };
  onSubs: { total: number; subs: number };
  onAo: number;
}

export function summarize(items: PunchItem[]): PunchSummary {
  const live = items.filter((i) => isOpenStatus(i.status));
  const crew = (i: PunchItem) => i.bic.type === 'assignee' && i.assignee?.type === 'ew_crew';
  const onUs = live.filter((i) => ballGroup(i) === 'ew');
  const onSubs = live.filter((i) => ballGroup(i) === 'assignee');
  return {
    total: items.length,
    open: items.filter((i) => OPEN_BUCKET.includes(i.status)).length,
    notSent: items.filter((i) => i.status === 'new').length,
    readyForReview: items.filter((i) => i.status === 'ready_for_review').length,
    awaitingApproval: items.filter((i) => i.status === 'verified').length,
    closed: items.filter((i) => i.status === 'closed').length,
    overdue: items.filter((i) => i.overdue).length,
    onUs: {
      total: onUs.length,
      toVerify: onUs.filter((i) => i.status === 'ready_for_review').length,
      toSend: onUs.filter((i) => i.status === 'new' && i.bic.type === 'ew').length,
      withCrew: onUs.filter(crew).length,
    },
    onSubs: { total: onSubs.length, subs: new Set(onSubs.map((i) => assigneeKey(i.assignee))).size },
    onAo: live.filter((i) => i.bic.type === 'ao').length,
  };
}

export interface SubRow {
  key: string;
  label: string;
  assignee: PunchAssigneeInput | null;
  open: number;
  review: number;
  overdue: number;
  closed: number;
  total: number;
  /** New items, which printing their sheet would mark sent. */
  unsent: number;
}

/** One row per assignee (sub, E&W crew, or Unassigned), most open first. */
export function bySub(items: PunchItem[]): SubRow[] {
  const rows = new Map<string, SubRow>();
  for (const i of items) {
    const key = assigneeKey(i.assignee);
    let row = rows.get(key);
    if (!row) {
      row = {
        key, label: i.assigneeLabel ?? 'Unassigned', assignee: i.assignee,
        open: 0, review: 0, overdue: 0, closed: 0, total: 0, unsent: 0,
      };
      rows.set(key, row);
    }
    row.total++;
    if (OPEN_BUCKET.includes(i.status)) row.open++;
    if (i.status === 'ready_for_review') row.review++;
    if (i.status === 'closed') row.closed++;
    if (i.status === 'new') row.unsent++;
    if (i.overdue) row.overdue++;
  }
  return [...rows.values()].sort((a, b) => b.open - a.open || a.label.localeCompare(b.label));
}

export type StatusFilter = '' | 'open' | 'ready_for_review' | 'verified' | 'closed' | 'overdue';
export type BicFilter = '' | PunchBicType;

export interface PunchFilters {
  q: string;
  sub: string;        // assigneeKey, or ''
  trade: string;      // trade key, or ''
  status: StatusFilter;
  bic: BicFilter;
}

export const NO_FILTERS: PunchFilters = { q: '', sub: '', trade: '', status: '', bic: '' };

export function hasFilters(f: PunchFilters): boolean {
  return !!(f.q.trim() || f.sub || f.trade || f.status || f.bic);
}

export function matches(i: PunchItem, f: PunchFilters): boolean {
  const q = f.q.trim().toLowerCase();
  if (q) {
    // "21", "#21" and "#021" all find item 21; otherwise match the text.
    const asNumber = /^#?\d+$/.test(q) ? Number(q.replace('#', '')) : null;
    const text = `${i.description} ${i.room ?? ''}`.toLowerCase();
    if (asNumber !== i.itemNo && !text.includes(q)) return false;
  }
  if (f.sub && assigneeKey(i.assignee) !== f.sub) return false;
  if (f.trade && i.trade !== f.trade) return false;
  if (f.bic && !(ballGroup(i) === f.bic && isOpenStatus(i.status))) return false;
  switch (f.status) {
    case '': break;
    case 'open': if (!OPEN_BUCKET.includes(i.status)) return false; break;
    case 'overdue': if (!i.overdue) return false; break;
    default: if (i.status !== f.status) return false;
  }
  return true;
}

/** Still-in-play items first, then newest first (design handoff, screen 1). */
export function sortItems(items: PunchItem[]): PunchItem[] {
  return [...items].sort((a, b) => {
    const ac = a.status === 'closed' ? 1 : 0;
    const bc = b.status === 'closed' ? 1 : 0;
    return ac - bc || b.itemNo - a.itemNo;
  });
}

export interface ItemAction {
  action: PunchAction | 'upload_after' | 'pass_to';
  label: string;
  variant: 'primary' | 'secondary' | 'danger';
  /** Needs a form before it can be sent (reject reason, after photo, pass target). */
  opens?: 'reject' | 'after_photo' | 'pass_to';
}

/**
 * The footer actions an item offers the caller, by status — mirroring the
 * API's lifecycle and access rules so only what the API would accept shows.
 * Pass To is offered separately (every non-closed item).
 */
/**
 * A live after photo no mark-fixed, reject or reopen has used up yet — added
 * since the last repair ended. The API accepts mark fixed on it without a new
 * upload, so the drawer offers that directly (Codex on #565).
 */
export function hasFreshAfterPhoto(item: Pick<PunchItem, 'media'>): boolean {
  return item.media.some((m) => m.kind === 'after' && !m.fixEvidence);
}

export function itemActions(
  item: Pick<PunchItem, 'status' | 'assignee' | 'assigneeLabel' | 'media'>,
  access: { canWork: boolean; canManage: boolean },
  requiresAoApproval: boolean,
): ItemAction[] {
  if (!access.canWork) return [];
  switch (item.status) {
    case 'new':
      return item.assignee
        ? [{ action: 'send', label: `Send to ${item.assigneeLabel}`, variant: 'primary' }]
        : [];
    case 'open':
    case 'rejected':
      return hasFreshAfterPhoto(item)
        ? [
          { action: 'mark_fixed', label: 'Upload New After Photo', variant: 'secondary', opens: 'after_photo' },
          { action: 'mark_fixed', label: 'Mark Fixed', variant: 'primary' },
        ]
        : [{ action: 'mark_fixed', label: 'Upload After Photo & Mark Fixed', variant: 'primary', opens: 'after_photo' }];
    case 'ready_for_review':
      return [
        { action: 'reject', label: 'Reject', variant: 'danger', opens: 'reject' },
        { action: 'accept', label: requiresAoApproval ? 'Accept' : 'Accept & Close', variant: 'primary' },
      ];
    case 'verified':
      return access.canManage
        ? [{ action: 'record_approval', label: 'Record Architect/Owner Approval', variant: 'primary' }]
        : [];
    case 'closed':
      return access.canManage ? [{ action: 'reopen', label: 'Reopen', variant: 'secondary' }] : [];
  }
}

/** Where Pass To can send the ball, given the item and the list. */
export function passTargets(
  item: Pick<PunchItem, 'status' | 'assignee' | 'bic'>,
  requiresAoApproval: boolean,
): { ew: boolean; assignee: boolean; ao: boolean } {
  if (item.status === 'closed') return { ew: false, assignee: false, ao: false };
  return {
    ew: true,
    assignee: !!item.assignee && item.bic.type !== 'assignee',
    ao: requiresAoApproval && item.bic.type !== 'ao',
  };
}

export interface CarryGroup {
  key: string;
  label: string;
  target: PunchAssigneeInput | { type: 'unassigned' };
  inPlay: number;
}

/** The statuses publishing sorts into carry-over or close (API: CARRY_OVER_STATUSES). */
export const CARRY_OVER_STATUSES: readonly PunchStatus[] = ['new', 'open', 'rejected', 'ready_for_review'];

/** One group per assignee with items still in play, for the publish flow's first step. */
export function carryOverGroups(items: PunchItem[]): CarryGroup[] {
  const groups = new Map<string, CarryGroup>();
  for (const i of items) {
    if (!CARRY_OVER_STATUSES.includes(i.status)) continue;
    const key = assigneeKey(i.assignee);
    const g = groups.get(key) ?? {
      key,
      label: i.assigneeLabel ?? 'Unassigned',
      target: i.assignee ?? { type: 'unassigned' as const },
      inPlay: 0,
    };
    g.inPlay++;
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) => b.inPlay - a.inPlay || a.label.localeCompare(b.label));
}

/**
 * The trades to offer when giving an item a trade: the job's, less the hidden
 * ones. An item's current trade stays pickable even if it has since been
 * hidden or removed, so opening an edit never silently changes it.
 */
export function pickableTrades(trades: PunchTrade[], current?: string | null): PunchTrade[] {
  const out = trades.filter((t) => !t.hidden);
  if (current && !out.some((t) => t.key.toLowerCase() === current.toLowerCase())) {
    out.unshift(trades.find((t) => t.key === current) ?? { key: current, label: current });
  }
  return out;
}

/** The trades to filter by: the pickable ones, plus any trade an item still uses. */
export function filterTrades(trades: PunchTrade[], items: Pick<PunchItem, 'trade'>[]): PunchTrade[] {
  const out = trades.filter((t) => !t.hidden);
  for (const i of items) {
    if (i.trade && !out.some((t) => t.key.toLowerCase() === i.trade!.toLowerCase())) {
      out.push(trades.find((t) => t.key === i.trade) ?? { key: i.trade, label: i.trade });
    }
  }
  return out;
}

/**
 * The field leader a new item gets when nobody picks one — the same rule the
 * server applies: the job's only field leader, or the person adding it if they
 * lead the job. null when it can't tell (several, or none).
 */
export function defaultFieldLeader(leaders: { id: string }[], meId: string | null | undefined): string | null {
  if (leaders.length === 1) return leaders[0].id;
  const me = meId ? leaders.find((l) => l.id.toLowerCase() === meId.toLowerCase()) : undefined;
  return me?.id ?? null;
}

/** A moment as the table and drawer show it: "Sep 29, 2:05 PM". */
export function stamp(v: string | null | undefined): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** How a trade key reads: its label, or the key itself for a trade since removed. */
export function tradeName(trades: PunchTrade[], key: string | null): string {
  return trades.find((t) => t.key === key)?.label ?? key ?? '—';
}

/**
 * The trades this dialog changed, vs. what it opened with, as the API takes
 * them: a sub name, or null to clear. The server merges them into the map
 * under its lock, so only these trades change (Codex on #565). Returns null
 * when nothing was changed here.
 */
export function tradeMapEdits(
  opening: Record<string, string>,
  edited: Record<string, string>,
): { trade: string; subName: string | null }[] | null {
  const norm = (v: string | undefined) => (v ?? '').trim().toLowerCase();
  const dirty = [...new Set([...Object.keys(opening), ...Object.keys(edited)])]
    .filter((t) => norm(opening[t]) !== norm(edited[t]))
    .sort();
  if (dirty.length === 0) return null;
  return dirty.map((trade) => ({ trade, subName: edited[trade] ? edited[trade] : null }));
}

/**
 * Whether the list already holds every value a settings patch sent — i.e. a
 * save that failed only in its reply actually landed (Codex on #565).
 */
export function settingsPatchApplied(patch: Record<string, unknown>, list: Record<string, unknown>): boolean {
  return Object.entries(patch).every(([k, v]) => (list[k] ?? null) === (v ?? null));
}

/**
 * Whether the map already holds every trade edit sent — a map save that failed
 * only in its reply actually landed (Codex on #565). Names compare without
 * regard to case or spaces, like everywhere subs are matched; null = cleared.
 */
export function tradeEditsApplied(
  changes: { trade: string; subName: string | null }[],
  map: { trade: string; subName: string }[],
): boolean {
  const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();
  const now = new Map(map.map((e) => [e.trade, e.subName]));
  return changes.every((c) => norm(now.get(c.trade)) === norm(c.subName));
}

/** An item's state as the drawer compares it after a failed step. */
export function itemFingerprint(i: Pick<PunchItem, 'status' | 'bic' | 'media' | 'updatedAt'>): string {
  return JSON.stringify([i.status, i.bic.type, i.bic.userId, i.media.length, i.updatedAt]);
}

/** "Sep 18", from a YYYY-MM-DD or ISO string; '' for null. */
export function shortDate(v: string | null | undefined, opts: { year?: boolean } = {}): string {
  if (!v) return '';
  const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', ...(opts.year ? { year: 'numeric' } : {}) };
  // A timestamp is a moment, shown as the reader's date. Reading its first ten
  // characters gave a UTC timestamp tomorrow's date after 8 p.m. Eastern
  // (review). A YYYY-MM-DD date names a day, so it is read with no shift.
  if (v.length > 10) {
    const at = new Date(v);
    return Number.isNaN(at.getTime()) ? '' : at.toLocaleDateString('en-US', fmt);
  }
  const [y, m, d] = v.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { ...fmt, timeZone: 'UTC' });
}

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/** The item's before photo link, if it has one. */
export function beforePhotoUrl(i: Pick<PunchItem, 'media'>): string | undefined {
  return i.media.find((m) => m.kind === 'before')?.webUrl;
}

// ── Sub sheets (#518) ────────────────────────────────────────────────────────

/** What a sub's sheet lists: the items waiting on them to fix (design, Export per sub). */
export const SHEET_STATUSES: readonly PunchStatus[] = ['new', 'open', 'rejected'];

export interface SheetGroup {
  key: string;                    // assigneeKey
  label: string;
  assignee: PunchAssigneeInput;
  items: PunchItem[];             // sorted by room
  newCount: number;               // New items: printing their sheet sends them
}

/**
 * Rooms in walking order: numbered rooms first, by number ("9" before "104",
 * "104" before "104A"), then named areas A→Z, then items with no room.
 */
export function compareRooms(a: string | null | undefined, b: string | null | undefined): number {
  const ra = (a ?? '').trim();
  const rb = (b ?? '').trim();
  if (!ra || !rb) return ra ? -1 : rb ? 1 : 0;
  const na = /^\d+/.exec(ra)?.[0];
  const nb = /^\d+/.exec(rb)?.[0];
  if (na && nb) return Number(na) - Number(nb) || ra.localeCompare(rb, undefined, { sensitivity: 'base' });
  if (na || nb) return na ? -1 : 1;
  return ra.localeCompare(rb, undefined, { sensitivity: 'base' });
}

/**
 * One sheet per assignee with something to fix, A→Z; each sheet's items by
 * room, then number. Unassigned items have nobody to hand a sheet to.
 */
export function sheetGroups(items: PunchItem[]): SheetGroup[] {
  const groups = new Map<string, SheetGroup>();
  for (const i of items) {
    if (!i.assignee || !SHEET_STATUSES.includes(i.status)) continue;
    const key = assigneeKey(i.assignee);
    let g = groups.get(key);
    if (!g) {
      g = { key, label: i.assigneeLabel ?? '', assignee: i.assignee, items: [], newCount: 0 };
      groups.set(key, g);
    }
    g.items.push(i);
    if (i.status === 'new') g.newCount++;
  }
  for (const g of groups.values()) {
    g.items.sort((x, y) => compareRooms(x.room, y.room) || x.itemNo - y.itemNo);
  }
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Who a sub texts the photo of a fix to: the field leaders on the sheet's
 * items, else the job's field leaders. [] when there are none to name.
 */
export function sheetContacts(items: Pick<PunchItem, 'fieldLeader'>[], jobLeaders: { name: string }[]): string[] {
  const named = [...new Set(items.map((i) => i.fieldLeader?.name.trim()).filter((n): n is string => !!n))];
  return named.length > 0 ? named : jobLeaders.map((l) => l.name);
}

// ── Phone list (#518, design "Punch List: mobile") ───────────────────────────

/**
 * Mine to verify: fixed and waiting on E&W — with me by name, or with E&W and
 * nobody named (whoever is on site can take it).
 */
export function isMineToVerify(i: Pick<PunchItem, 'status' | 'bic'>, meId: string | null | undefined): boolean {
  if (i.status !== 'ready_for_review' || i.bic.type !== 'ew') return false;
  return !i.bic.userId || (!!meId && i.bic.userId.toLowerCase() === meId.toLowerCase());
}

/** What the phone list is filtered to: one choice at a time, from the tiles, chips and sheets. */
export type PhoneFilter =
  | { kind: 'all' }
  | { kind: 'on_us' }
  | { kind: 'on_subs' }
  | { kind: 'overdue' }
  | { kind: 'verify' }
  | { kind: 'sub'; key: string; label: string }
  | { kind: 'room'; room: string };

export function phoneMatches(i: PunchItem, f: PhoneFilter, meId: string | null | undefined): boolean {
  switch (f.kind) {
    case 'all': return true;
    // The same grouping as the desktop's Ball in court card and its counts:
    // the E&W crew doing the work counts as on us.
    case 'on_us': return isOpenStatus(i.status) && ballGroup(i) === 'ew';
    case 'on_subs': return isOpenStatus(i.status) && ballGroup(i) === 'assignee';
    case 'overdue': return i.overdue;
    case 'verify': return isMineToVerify(i, meId);
    case 'sub': return assigneeKey(i.assignee) === f.key;
    case 'room': return roomKey(i.room) === roomKey(f.room);
  }
}

/** Rooms compare without regard to case or spaces; no room is its own group. */
export function roomKey(room: string | null | undefined): string {
  return (room ?? '').trim().toLowerCase();
}

/** "Room 104" for a numbered room, the area's own name otherwise, "No room" for none. */
export function roomTitle(room: string | null | undefined): string {
  const r = (room ?? '').trim();
  if (!r) return 'No room';
  return /^\d+[a-z]?$/i.test(r) ? `Room ${r}` : r;
}

export interface RoomGroup { key: string; room: string; items: PunchItem[]; open: number }

/** Items grouped by room in walking order (numbered rooms, then areas, then none); newest first inside. */
export function roomGroups(items: PunchItem[]): RoomGroup[] {
  const groups = new Map<string, RoomGroup>();
  for (const i of items) {
    const key = roomKey(i.room);
    let g = groups.get(key);
    if (!g) { g = { key, room: (i.room ?? '').trim(), items: [], open: 0 }; groups.set(key, g); }
    g.items.push(i);
    if (i.status !== 'closed') g.open++;
  }
  for (const g of groups.values()) g.items = sortItems(g.items);
  return [...groups.values()].sort((a, b) => compareRooms(a.room, b.room));
}

/** The room stepper's − / +: a plain room number moves by one; anything else stays as typed. */
export function stepRoom(room: string, delta: 1 | -1): string {
  const r = room.trim();
  if (!r) return delta === 1 ? '1' : room;
  if (!/^\d+$/.test(r)) return room;
  const n = Math.max(0, Number(r) + delta);
  return String(n).padStart(r.length, '0');
}

/** "normal" → "Normal". */
export function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Recently used rooms, most recent first, once each (case aside), at most `max`. */
export function pushRecentRoom(recent: string[], room: string, max = 6): string[] {
  const r = room.trim();
  if (!r) return recent;
  return [r, ...recent.filter((x) => roomKey(x) !== roomKey(r))].slice(0, max);
}

// ── Bulk changes (#518 phase 2, design "Bulk bar") ─────────────────────────

/** The most ids one bulk request takes (the API's cap). */
export const BULK_MAX = 200;

/** Ids in requests of at most `size`, in order. */
export function chunk<T>(xs: T[], size = BULK_MAX): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size));
  return out;
}

/**
 * The selection after a bulk change: the current one (ticks made while it ran
 * included) less what's done — the items that changed, and the ones already
 * so (they have what was asked; ticked, they'd only be in the way of the next
 * change). What was skipped, or left untried or unconfirmed by a stop, stays
 * ticked — the items worth another look or a retry.
 */
export function untickChanged(current: Set<number>, results: { itemId: number; ok: boolean }[]): Set<number> {
  const done = new Set(results.filter((r) => r.ok).map((r) => r.itemId));
  return new Set([...current].filter((id) => !done.has(id)));
}

export interface BulkOutcome { ok: string | null; skipped: string[]; skippedIds: number[]; error: string | null }

/** What a bulk change did, for the note under the bar. */
export function bulkOutcome(
  results: { itemId: number; ok: boolean; unchanged?: boolean; error?: string }[],
  what: string,
  labelOf: (id: number) => string,
  error: string | null = null,
): BulkOutcome {
  // An item already so (same date, same assignee) is fine but isn't a change.
  const changed = results.filter((r) => r.ok && !r.unchanged).length;
  const already = results.filter((r) => r.ok && r.unchanged).length;
  const bad = results.filter((r) => !r.ok);
  const alreadyText = already ? `${already} already ${already === 1 ? 'was' : 'were'}.` : '';
  return {
    ok: changed
      ? `${what} for ${changed} item${changed === 1 ? '' : 's'}.${alreadyText ? ` ${alreadyText}` : ''}`
      : alreadyText ? `Nothing to change: ${alreadyText}` : null,
    skipped: bad.map((r) => `${labelOf(r.itemId)}: ${r.error ?? 'not changed'}`),
    skippedIds: bad.map((r) => r.itemId),
    error,
  };
}

// ── Sheet email text (#518 phase 2, design "Copy Email Text") ────────────────

export interface SheetEmail { to: string; subject: string; body: string }

/**
 * The email draft for one sub's sheet: the same items, order and contact line
 * as the printed sheet, as plain text to paste into Outlook. No addresses:
 * the Project Tracker doesn't store the subs' emails.
 */
export function sheetEmail(g: SheetGroup, o: {
  jobNumber: string;
  jobTitle: string;
  contacts: string[];
  tradeLabel: (key: string | null) => string;
  reasonLabel: (key: string) => string;
}): SheetEmail {
  const job = `Job ${o.jobNumber}${o.jobTitle ? ` · ${o.jobTitle}` : ''}`;
  const n = g.items.length;
  const photoTo = o.contacts.length > 0
    ? `text a photo of the finished work with the item number to ${o.contacts.join(', ')} (field leader${o.contacts.length > 1 ? 's' : ''})`
    : 'send a photo of the finished work with the item number to your E&W contact';
  const lines = g.items.map((i) => {
    const loc = locationLabel(i);
    const head = `${i.label}${loc !== '—' ? ` · ${loc}` : ''} — ${i.description}`;
    const parts = [
      o.tradeLabel(i.trade),
      i.dueDate ? `due ${shortDate(i.dueDate)}` : 'no due date',
      i.status === 'rejected' ? `sent back${i.rejectReason ? `: ${o.reasonLabel(i.rejectReason)}` : ''}` : null,
    ].filter(Boolean);
    const notes = i.notes ? `   Notes: ${i.notes.trim().split(/\r?\n/).join('\n          ')}` : null;
    return [`${head} (${parts.join(', ')})`, notes].filter(Boolean).join('\n');
  });
  return {
    to: g.label,
    subject: `Punch list — ${job} — ${n} item${n === 1 ? '' : 's'} for ${g.label}`,
    body: [
      `Hi ${g.label},`,
      '',
      `Here ${n === 1 ? 'is the punch item' : `are the ${n} punch items`} for you on ${job}. Please fix each by its due date and ${photoTo}. We verify on site before closing.`,
      '',
      ...lines,
      '',
      'Thank you,',
      'East & Westbrook Construction',
    ].join('\n'),
  };
}

/**
 * What goes on the clipboard: one draft is just its body (pasted into a new
 * email, whose subject is copied on its own); several go under dividers, each
 * with its subject, to split into one email per sub.
 */
export function joinEmails(emails: SheetEmail[]): string {
  if (emails.length === 1) return emails[0].body;
  return emails.map((e) => `===== ${e.to} =====\nSubject: ${e.subject}\n\n${e.body}`).join('\n\n\n');
}

// ── Video clips (#518 phase 2) ──────────────────────────────────────────────

/**
 * A video's history step keeps the photo action names (photo_added /
 * photo_removed, note "Video"), so the history and the feed contract are
 * unchanged; this is how it reads. Null for anything else.
 */
export function videoStepLabel(e: { action: string; note: string | null }): string | null {
  if (e.note !== 'Video') return null;
  if (e.action === 'photo_added') return 'Video added';
  if (e.action === 'photo_removed') return 'Video removed';
  return null;
}

/** How many clips an item has, as the report says it: "1 video in the Job Folder". */
export function videoCountLine(item: Pick<PunchItem, 'media'>): string | null {
  const n = item.media.filter((m) => m.kind === 'video').length;
  return n ? `${n} video${n === 1 ? '' : 's'} in the Job Folder` : null;
}

// ── Close-out report (#518 phase 2) ─────────────────────────────────────────

/**
 * The photo of the repair that closed the item: the newest after photo a
 * mark-fixed relied on, else the newest after photo.
 */
export function finalAfterPhoto(item: Pick<PunchItem, 'media'>): PunchMedia | null {
  const after = item.media.filter((m) => m.kind === 'after');
  const newest = (xs: PunchMedia[]) => xs.reduce<PunchMedia | null>((a, m) => (!a || m.uploadedAt > a.uploadedAt ? m : a), null);
  return newest(after.filter((m) => m.fixEvidence)) ?? newest(after);
}

export type ReportItem = PunchItem & { signoffs: PunchSignoffs };

export interface ReportSummary {
  total: number;
  internal: number;
  addedAtWalk: number;
  /** Closed at publish, not carried over to the published list. */
  notCarriedOver: number;
  rejectedOnce: number;
  /** With approval on: items closed through the approval step, and the ones that weren't (none at publish). */
  approved: number;
  needingApproval: number;
  /**
   * One row per assignee, by name; Unassigned last. Items are counted under
   * who has the item now; each rejection under whose fix it turned down, so a
   * sub the item moved away from keeps its rejections (review).
   */
  subs: { label: string; items: number; rejections: number }[];
}

/**
 * Rows are grouped by the same typed, case-insensitive key as the tab's
 * by-sub card (assigneeKey), not by the name as written: a vendor spelled
 * "ABC PAINTING" on older items is the same sub as "ABC Painting", and a sub
 * actually named "Unassigned" is not the unassigned items (Codex on #583).
 * A rejection step names its assignee as text, so the E&W crew is known by
 * its label. The row shows the first spelling met, current assignees first.
 */
export function reportSummary(items: ReportItem[], ewCrewLabel: string): ReportSummary {
  const subs = new Map<string, { key: string; label: string; items: number; rejections: number }>();
  const rowFor = (key: string, label: string) => {
    let row = subs.get(key);
    if (!row) { row = { key, label, items: 0, rejections: 0 }; subs.set(key, row); }
    return row;
  };
  const keyOfName = (name: string) => (name === ewCrewLabel ? 'ew_crew' : assigneeKey({ type: 'sub', name }));
  for (const i of items) rowFor(assigneeKey(i.assignee), i.assigneeLabel ?? 'Unassigned').items++;
  let internal = 0; let notCarriedOver = 0; let rejectedOnce = 0; let approved = 0; let needingApproval = 0;
  for (const i of items) {
    if (i.origin === 'internal') internal++;
    const skipped = !!i.signoffs.closed?.notCarriedOver;
    if (skipped) notCarriedOver++;
    else { needingApproval++; if (i.signoffs.approved) approved++; }
    if (i.signoffs.rejections > 0) rejectedOnce++;
    // A step that names nobody counts under who has the item now.
    for (const who of i.signoffs.rejectedFor) {
      if (who) rowFor(keyOfName(who), who).rejections++;
      else rowFor(assigneeKey(i.assignee), i.assigneeLabel ?? 'Unassigned').rejections++;
    }
  }
  const rows = [...subs.values()]
    .sort((a, b) => (a.key === 'unassigned' ? 1 : 0) - (b.key === 'unassigned' ? 1 : 0) || a.label.localeCompare(b.label))
    .map(({ label, items: n, rejections }) => ({ label, items: n, rejections }));
  return {
    total: items.length, internal, addedAtWalk: items.length - internal, notCarriedOver, rejectedOnce,
    approved, needingApproval, subs: rows,
  };
}
